import PlatformSwitch from "@/components/admin/collections/PlatformSwitch";
import ScopeBadge from "@/components/admin/collections/ScopeBadge";
import { COLUMN_LABELS, CUSTOM_SWITCH_LOCKED_TITLE } from "@/constants/adminCollections";
import type { AdminCollectionRow } from "@/interfaces/adminCollections";
import { formatCollectionValue, formatSyncedDate } from "@/utils/adminCollections-helper";

interface CollectionsTableProps {
  rows: readonly AdminCollectionRow[];
  pendingIds: ReadonlySet<string>;
  onToggle: (row: AdminCollectionRow, next: boolean) => void;
  onOpen: (row: AdminCollectionRow) => void;
}

const TH_CLASS =
  "bg-wax px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[1px] whitespace-nowrap text-rodeo-black";
const TD_CLASS = "border-b border-smoke px-4 py-3.5 text-sm whitespace-nowrap text-rodeo-black";

export default function CollectionsTable({
  rows,
  pendingIds,
  onToggle,
  onOpen,
}: CollectionsTableProps) {
  return (
    <div className="overflow-auto rounded-(--radius-card) border border-card-border bg-white">
      <table className="w-full min-w-[900px] border-collapse">
        <thead>
          <tr>
            {Object.values(COLUMN_LABELS).map((label) => (
              <th key={label} scope="col" className={TH_CLASS}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              data-collection-id={row.id}
              onClick={(event) => {
                if (event.target instanceof Element && event.target.closest('[role="switch"]'))
                  return;
                onOpen(row);
              }}
              className="cursor-pointer hover:bg-wax"
            >
              <td className={TD_CLASS}>
                <button
                  type="button"
                  className="cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-bold text-rodeo-black hover:underline"
                >
                  {row.name}
                </button>
                {row.slug !== null && (
                  <div className="font-mono text-[12px] text-graphite">{row.slug}</div>
                )}
              </td>
              <td className={TD_CLASS}>{formatCollectionValue(row.value)}</td>
              <td className={TD_CLASS}>{row.region}</td>
              <td className={TD_CLASS}>{row.productCount}</td>
              <td className={TD_CLASS}>
                <ScopeBadge scope={row.scope} />
              </td>
              <td className={TD_CLASS}>
                <PlatformSwitch
                  name={row.name}
                  checked={row.platformEnabled}
                  disabled={pendingIds.has(row.id) || row.isCustom}
                  title={row.isCustom ? CUSTOM_SWITCH_LOCKED_TITLE : undefined}
                  onToggle={() => onToggle(row, !row.platformEnabled)}
                />
              </td>
              <td className={TD_CLASS}>{formatSyncedDate(row.lastSyncedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
